import { Icons } from '@/assets/icons/icons';

import { Callout } from './callout';

import type { ParseFlag } from '@littlebird/axon-mcp/contract';

type PlaceholderEnvFlag = `placeholder-env:${string}`;
type PlaceholderHeaderFlag = `placeholder-header:${string}`;
type SimpleFlag = Exclude<
  ParseFlag,
  PlaceholderEnvFlag | PlaceholderHeaderFlag
>;

const isPlaceholderEnv = (flag: ParseFlag): flag is PlaceholderEnvFlag =>
  flag.startsWith('placeholder-env:');
const isPlaceholderHeader = (flag: ParseFlag): flag is PlaceholderHeaderFlag =>
  flag.startsWith('placeholder-header:');

const SIMPLE_FLAG_COPY: Record<SimpleFlag, string> = {
  'secret-in-args':
    'A credential appears in the arguments — consider moving it to an environment variable',
  'sse-endpoint': 'Legacy SSE endpoint — confirm the URL is right',
  'unwrapped-bridge': 'Converted a local proxy config to a direct URL',
  'synthesized-auth-header':
    'Built an Authorization header from the pasted token — verify it',
  'python-m-no-package-spec':
    'Runs a Python module directly — make sure that Python and the module exist on this device',
  'uvx-from': 'Package comes from a --from distribution',
  'uvx-with-extra-dep':
    'Extra dependency from --with was kept in the arguments',
  'uv-run-wraps-python':
    'uv run wraps a direct Python invocation — runs as a program, not a managed package',
};

function flagCopy(flag: ParseFlag): string {
  if (isPlaceholderEnv(flag)) {
    return `Fill in a real value for ${flag.slice('placeholder-env:'.length)}`;
  }
  if (isPlaceholderHeader(flag)) {
    return `Fill in a real value for the ${flag.slice('placeholder-header:'.length)} header`;
  }
  return SIMPLE_FLAG_COPY[flag];
}

export function JsonFlagsNotice({ flags }: { flags: ParseFlag[] }) {
  return (
    <Callout tone="neutral" icon={<Icons.warnTriangle className="size-4" />}>
      <ul className="list-disc space-y-0.5 pl-4">
        {flags.map((flag) => (
          <li key={flag}>{flagCopy(flag)}</li>
        ))}
      </ul>
    </Callout>
  );
}
